import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Prompts queued while an agent-chat turn is still streaming.
 *
 * Queues are keyed per conversation (`org:agent:conversation`, see
 * promptQueueKey) so two open chats never drain each other, and persist to
 * localStorage so a queue survives a reload. Dispatch is not the store's
 * job: use-prompt-queue-dispatch watches the live turn and pulls from here.
 *
 * The hold flags are deliberately NOT persisted. A hold exists to stop the
 * queue auto-firing into a turn that just failed (or was stopped by hand);
 * after a reload nothing auto-fires anyway -- dispatch only triggers on a
 * turn-completion transition -- so a persisted hold would only go stale.
 */
export interface QueuedPrompt {
  id: string;
  text: string;
  queuedAt: number;
}

/**
 * Ceiling per conversation. Anyone queueing this many prompts mid-turn is
 * far outside the intended flow, and an unbounded queue would grow
 * localStorage without end -- drop the oldest, loudly.
 */
export const MAX_QUEUED_PROMPTS = 20;

/**
 * Persisted entries older than this are dropped on rehydrate. A queue
 * abandoned weeks ago would otherwise auto-fire the moment its
 * conversation's next manual turn completes -- long after the user has
 * forgotten the prompts were ever queued.
 */
export const MAX_PROMPT_AGE_MS = 24 * 60 * 60 * 1000;

/** One queue per conversation, scoped so agents/orgs never collide. */
export function promptQueueKey(
  orgId: string | undefined,
  agentId: string | undefined,
  conversationId: string | null,
): string {
  return `${orgId ?? ''}:${agentId ?? ''}:${conversationId ?? 'draft'}`;
}

interface ChatPromptQueueState {
  queues: Record<string, QueuedPrompt[]>;
  /**
   * Conversations whose auto-dispatch is paused -- set after a turn error,
   * a manual stop, or a turn that ended waiting on a decision. Memory-only
   * (see module doc).
   */
  heldKeys: Record<string, true>;
  enqueue: (key: string, text: string) => void;
  remove: (key: string, id: string) => void;
  /** Remove and return one item, for click-to-edit. */
  take: (key: string, id: string) => QueuedPrompt | null;
  /** Remove and return the oldest item (FIFO dispatch). */
  dequeue: (key: string) => QueuedPrompt | null;
  /**
   * Put a dequeued item back at the head: the send path refused it (most
   * likely another turn already had the stream), so nothing went out and
   * it must stay next in line.
   */
  requeueFront: (key: string, item: QueuedPrompt) => void;
  clear: (key: string) => void;
  setHeld: (key: string, held: boolean) => void;
  /**
   * Move a queue to a new key. A brand-new chat queues under a draft key
   * until the first turn's start frame mints the conversation id.
   */
  reassign: (fromKey: string, toKey: string) => void;
}

/** Queues map with `key` set to `items`, or dropped entirely when empty. */
function withQueue(
  queues: Record<string, QueuedPrompt[]>,
  key: string,
  items: QueuedPrompt[],
): Record<string, QueuedPrompt[]> {
  if (items.length) return { ...queues, [key]: items };
  const rest = { ...queues };
  delete rest[key];
  return rest;
}

/** Held map without `key` -- an empty queue has nothing left to hold. */
function withoutHeld(
  heldKeys: Record<string, true>,
  key: string,
): Record<string, true> {
  if (!heldKeys[key]) return heldKeys;
  const rest = { ...heldKeys };
  delete rest[key];
  return rest;
}

export const useChatPromptQueueStore = create<ChatPromptQueueState>()(
  persist(
    (set, get) => ({
      queues: {},
      heldKeys: {},

      enqueue: (key, text) => {
        const trimmed = text.trim();
        if (!trimmed) return;
        set((state) => {
          let items = state.queues[key] ?? [];
          if (items.length >= MAX_QUEUED_PROMPTS) {
            console.warn(
              `[chat-prompt-queue] queue for ${key} is capped at ` +
                `${MAX_QUEUED_PROMPTS} prompts; dropping the oldest`,
            );
            items = items.slice(items.length - (MAX_QUEUED_PROMPTS - 1));
          }
          return {
            queues: {
              ...state.queues,
              [key]: [
                ...items,
                {
                  id: crypto.randomUUID(),
                  text: trimmed,
                  queuedAt: Date.now(),
                },
              ],
            },
          };
        });
      },

      remove: (key, id) => {
        set((state) => {
          const items = state.queues[key];
          if (!items?.some((item) => item.id === id)) return {};
          const next = items.filter((item) => item.id !== id);
          return {
            queues: withQueue(state.queues, key, next),
            ...(next.length
              ? {}
              : { heldKeys: withoutHeld(state.heldKeys, key) }),
          };
        });
      },

      take: (key, id) => {
        const item = get().queues[key]?.find((entry) => entry.id === id);
        if (!item) return null;
        get().remove(key, id);
        return item;
      },

      dequeue: (key) => {
        const items = get().queues[key];
        if (!items?.length) return null;
        const [next, ...rest] = items;
        set((state) => ({
          queues: withQueue(state.queues, key, rest),
          ...(rest.length
            ? {}
            : { heldKeys: withoutHeld(state.heldKeys, key) }),
        }));
        return next;
      },

      requeueFront: (key, item) => {
        set((state) => {
          const items = state.queues[key] ?? [];
          if (items.some((entry) => entry.id === item.id)) return {};
          return {
            queues: {
              ...state.queues,
              [key]: [item, ...items].slice(0, MAX_QUEUED_PROMPTS),
            },
          };
        });
      },

      clear: (key) => {
        set((state) => ({
          queues: withQueue(state.queues, key, []),
          heldKeys: withoutHeld(state.heldKeys, key),
        }));
      },

      setHeld: (key, held) => {
        set((state) => {
          if (held) {
            // A hold on nothing would silently block the next thing queued.
            if (!state.queues[key]?.length || state.heldKeys[key]) return {};
            return { heldKeys: { ...state.heldKeys, [key]: true } };
          }
          return { heldKeys: withoutHeld(state.heldKeys, key) };
        });
      },

      reassign: (fromKey, toKey) => {
        if (fromKey === toKey) return;
        set((state) => {
          const moving = state.queues[fromKey];
          if (!moving?.length) return {};
          // The target key having its own queue means the id was reused
          // across a draft handoff race; keep both, oldest first.
          const merged = [...(state.queues[toKey] ?? []), ...moving];
          if (merged.length > MAX_QUEUED_PROMPTS) {
            console.warn(
              `[chat-prompt-queue] queue for ${toKey} is capped at ` +
                `${MAX_QUEUED_PROMPTS} prompts; dropping the oldest`,
            );
          }
          const rest = { ...state.queues };
          delete rest[fromKey];
          return {
            queues: { ...rest, [toKey]: merged.slice(-MAX_QUEUED_PROMPTS) },
            heldKeys: state.heldKeys[fromKey]
              ? { ...withoutHeld(state.heldKeys, fromKey), [toKey]: true }
              : state.heldKeys,
          };
        });
      },
    }),
    {
      name: 'agent-chat-prompt-queue',
      version: 1,
      // Holds are session-scoped by design; only the queues survive reload.
      partialize: (state) => ({ queues: state.queues }),
      // Age out stale entries on load (see MAX_PROMPT_AGE_MS).
      merge: (persisted, current) => {
        const persistedQueues =
          (persisted as { queues?: Record<string, QueuedPrompt[]> } | null)
            ?.queues ?? {};
        const cutoff = Date.now() - MAX_PROMPT_AGE_MS;
        const queues: Record<string, QueuedPrompt[]> = {};
        for (const [key, items] of Object.entries(persistedQueues)) {
          const fresh = items.filter((item) => item.queuedAt > cutoff);
          if (fresh.length) queues[key] = fresh;
        }
        return { ...current, queues };
      },
    },
  ),
);
